// modules/elearning/courses/CourseFilter.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  Eraser,
  BookOpen,
  LayoutGrid,
  Activity,
  FileSpreadsheet,
  MoreVertical,
  UserCog,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { hasPermission } from "../../../utils/permissionUtils";

const CourseFilter = ({ onFilterChange, onNewClick, onImportExcelClick, onAssignClick }) => {
  const [filters, setFilters] = useState({
    keyword: "",
    subject_id: "",
    classify: "",
    is_active: "",
  });

  const [advanced, setAdvanced] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);

  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  // ================= Responsive detect =================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ================= Load subjects =================
  useEffect(() => {
    getSubjectsLookup()
      .then((res) =>
        setSubjectOptions(
          (res || []).map((s) => ({ value: s.id, label: s.name }))
        )
      )
      .catch(console.error);
  }, []);

  // ================= Debounce keyword =================
  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]);

  const updateFilter = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    if (key !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = { keyword: "", subject_id: "", classify: "", is_active: "" };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-courses-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  // ================= MOBILE UI =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 text-left">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm khóa học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all outline-none"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(true)} active={advanced} />
          {hasPermission("elearning.courses", "C") && onImportExcelClick && (
            <ActionButton
              icon={FileSpreadsheet}
              onClick={() => onImportExcelClick()}
              title="Nhập khóa học từ Excel"
            />
          )}
        </div>

        {/* FLOATING ADD BUTTON FOR MOBILE */}
        {hasPermission("elearning.courses", "C") && (
          <button
            onClick={onNewClick}
            className="fixed bottom-24 right-6 bg-red-600 text-white w-14 h-14 rounded-full shadow-lg shadow-red-200 flex items-center justify-center hover:scale-105 active:scale-95 transition-all z-40"
          >
            <Plus size={24} />
          </button>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc khóa học"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 py-4 border border-gray-200 text-gray-600 rounded-2xl font-bold"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-gray-400 uppercase ml-1 flex items-center gap-1.5"><BookOpen size={12} /> Môn học</label>
              <Select
                styles={customSelectStyles}
                options={subjectOptions}
                placeholder="Chọn môn học..."
                value={subjectOptions.find(o => o.value === filters.subject_id) || null}
                onChange={(opt) => updateFilter("subject_id", opt?.value || "")}
                isClearable
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-gray-400 uppercase ml-1 flex items-center gap-1.5"><LayoutGrid size={12} /> Phân loại</label>
              <Select
                styles={customSelectStyles}
                placeholder="Chọn phân loại..."
                options={[
                  { value: "truong-hoc", label: "Trường học" },
                  { value: "trung-tam", label: "Trung tâm" },
                ]}
                value={filters.classify ? { value: filters.classify, label: filters.classify === "truong-hoc" ? "Trường học" : "Trung tâm" } : null}
                onChange={(opt) => updateFilter("classify", opt?.value || "")}
                isClearable
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-gray-400 uppercase ml-1 flex items-center gap-1.5"><Activity size={12} /> Trạng thái</label>
              <Select
                styles={customSelectStyles}
                placeholder="Chọn trạng thái..."
                options={[
                  { value: "true", label: "Đang dùng" },
                  { value: "false", label: "Ngưng sử dụng" },
                ]}
                value={filters.is_active ? { value: filters.is_active, label: filters.is_active === "true" ? "Đang dùng" : "Ngưng sử dụng" } : null}
                onChange={(opt) => updateFilter("is_active", opt?.value || "")}
                isClearable
              />
            </div>
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP UI =================
  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 text-left relative">
      <div className="flex items-center justify-between gap-4">
        {/* LEFT: Search & Action Buttons */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm khóa học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => onFilterChange?.({ ...filters })} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />

          {(filters.subject_id || filters.classify || filters.is_active) && (
            <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {/* RIGHT: Import + Add */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {hasPermission("elearning.courses.assign", "R") && onAssignClick && (
            <button
              type="button"
              onClick={() => onAssignClick()}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-bold hover:bg-gray-50 hover:border-red-200 hover:text-red-700 shadow-sm transition-all active:scale-95 uppercase text-[11px] tracking-wider"
            >
              <UserCog size={18} />
              Gán khóa học
            </button>
          )}

          {hasPermission("elearning.courses", "C") && onImportExcelClick && (
            <button
              type="button"
              onClick={() => onImportExcelClick()}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-red-200 text-red-700 rounded-full font-bold hover:bg-red-50 shadow-sm transition-all active:scale-95 uppercase text-[11px] tracking-wider"
            >
              <FileSpreadsheet size={18} />
              Nhập Excel
            </button>
          )}

          {/* Nút Thêm mới */}
          {hasPermission("elearning.courses", "C") && (
            <button
              onClick={onNewClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <Plus size={18} />
              Thêm khóa học
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${
                moreOpen
                  ? "bg-gray-100 border-gray-300"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <MoreVertical size={18} />
            </button>

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 z-50 mt-3 w-56 overflow-hidden rounded-[1.5rem] border border-gray-100 bg-white p-2 shadow-2xl"
                  onMouseLeave={() => setMoreOpen(false)}
                >
                  <button
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-xs font-bold text-gray-600 transition-all hover:bg-red-50 hover:text-red-600"
                    onClick={() => {
                      openGuide();
                      setMoreOpen(false);
                    }}
                  >
                    <BookOpen size={16} />
                    HƯỚNG DẪN SỬ DỤNG
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ADVANCED FILTER ROW DESKTOP */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-visible"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase ml-1">Môn học</label>
                <Select
                  styles={customSelectStyles}
                  options={subjectOptions}
                  placeholder="Tất cả môn học"
                  value={subjectOptions.find(o => o.value === filters.subject_id) || null}
                  onChange={(opt) => updateFilter("subject_id", opt?.value || "")}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase ml-1">Phân loại</label>
                <Select
                  styles={customSelectStyles}
                  placeholder="Tất cả phân loại"
                  options={[
                    { value: "truong-hoc", label: "Trường học" },
                    { value: "trung-tam", label: "Trung tâm" },
                  ]}
                  value={filters.classify ? { value: filters.classify, label: filters.classify === "truong-hoc" ? "Trường học" : "Trung tâm" } : null}
                  onChange={(opt) => updateFilter("classify", opt?.value || "")}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase ml-1">Trạng thái</label>
                <Select
                  styles={customSelectStyles}
                  placeholder="Tất cả trạng thái"
                  options={[
                    { value: "true", label: "Đang dùng" },
                    { value: "false", label: "Ngưng sử dụng" },
                  ]}
                  value={filters.is_active ? { value: filters.is_active, label: filters.is_active === "true" ? "Đang dùng" : "Ngưng sử dụng" } : null}
                  onChange={(opt) => updateFilter("is_active", opt?.value || "")}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CourseFilter;